import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Container,
  Stack,
  Avatar,
  Badge,
  Fab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Drawer,
  Tooltip,
  Zoom,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';

import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  AttachMoney as MoneyIcon,
  Message as MessageIcon,
  Handshake as HandshakeIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Analytics as AnalyticsIcon,
  Article as ArticleIcon,
  Notifications as NotificationsIcon,
  Description as ContractIcon,
  ViewModule as ViewModuleIcon,
  List as ListIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  ExpandMore,
  ExpandLess,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';

// Import your existing view components
import CompanyDetailsView from './sections/companydetailsviews';
import UnifiedCollaborationAdminView from './sections/collabsadminview';
import InvoiceDetailsView from './sections/invoicedetailsviews';
import PayoutDetailsView from './sections/payoutdetailsview';
import CreatorDetailsView from './sections/creatordetailsview';
import UserDetailsView from './sections/admindetailviews';
import MarketingView from './sections/marketingComponents/marketingdetailview';
import CreatorCRMView from './sections/creatorCRM/creatorCRMview';
import CreatorSMS from './sections/creatorCRM/creatorSMS';
import useAuth from '../../../Hooks/use-auth';
import UserAdminView from './sections/newUserParntershipComponents/userdetailsview';
import ReferralDetailsView from './sections/newUserParntershipComponents/referralsdetailsview';
import SMSChannels from './sections/newUserParntershipComponents/textchannels';
import ClientDetailsView from './sections/newUserParntershipComponents/clientdetailsview';
import CreatorEmailView from './sections/marketingComponents/creatoremails';
import InternalCampaigns from './sections/marketingComponents/internalcampaigns';
import Blocklist from './sections/blocklist';
import BrandEmails from './sections/brandemails';
import BlastEmail from './sections/marketingComponents/blastemail';
import AdminServices from './sections/services';
import Withdrawals from './sections/Withdrawals/withdrawals';
import SurveyDetailsView from './sections/surveydetailsview';
import BlitzBoltDetailsView from './sections/blitzboltsview';
import NotificationsDetailsView from './sections/adminComponents/notifs';
import ConversationsAdminView from './sections/newUserParntershipComponents/convo';
import ContractDetailsView from './userCompanyTools/contractmgmt';
import NOADetailsView from './sections/Withdrawals/noadetailview';
import ArticleAdminView from './sections/creatorCRM/articleadmin';
import BlitzSystemOverview from './sections/blitzsystemsoverview';
import CollectionsDetailsView from './sections/collections/collectionsdetailsview';
import BankAccountsSection from './sections/BankAccounts/index';
import PlatformPaymentMethodsSection from './sections/PlatformPaymentMethods/index';
import UserBalancesView from './sections/userbalancesview';
import PaymentSettingsView from './sections/PaymentSettings';
import CommerceInitView from './sections/commerceInit';

// Constants for AppShell bottom nav spacing
const APPSHELL_BAR_HEIGHT = 86; // matches AppShell BAR_HEIGHT
const SPEED_DIAL_MARGIN = 16;   // additional margin for spacing

// Organized navigation structure
const navigationSections = [
  {
    title: 'Company & Admin',
    icon: BusinessIcon,
    color: 'primary',
    items: [
      { label: 'System Overview', value: 'system-overview', icon: DashboardIcon, description: 'View system-wide financial metrics and health' },
      { label: 'Company Details', value: 'companies', icon: BusinessIcon, description: 'Manage company information and settings' },
      { label: 'KPIs & Analytics', value: 'admin', icon: AnalyticsIcon, description: 'View performance metrics and analytics' },
      { label: 'Users', value: 'users', icon: PeopleIcon, description: 'Manage user accounts and permissions' },
    ]
  },
  {
    title: 'Financial',
    icon: MoneyIcon,
    color: 'warning',
    items: [
      { label: 'Payouts', value: 'payouts', icon: MoneyIcon, description: 'Manage creator payouts and payments' },
      { label: 'Invoices', value: 'invoices', icon: ArticleIcon, description: 'Handle billing and invoicing' },
      { label: 'Withdrawals', value: 'withdrawals', icon: MoneyIcon, description: 'Process withdrawal requests' },
      { label: 'NOA Management', value: 'noa', icon: AssignmentIcon, description: 'Manage assignment notices' },
      { label: 'Collections', value: 'collections', icon: AssignmentIcon, description: 'Manage collections for overdue invoices' },
      { label: 'Bank Accounts', value: 'bankAccounts', icon: MoneyIcon, description: 'Manage company bank accounts for ACH reimbursements' },
      { label: 'Platform Payment Methods', value: 'platformPaymentMethods', icon: MoneyIcon, description: 'Manage platform payment methods for Stripe payout operations' },
      { label: 'Blitz T2', value: 'userBalances', icon: MoneyIcon, description: 'Track all user and creator balances' },
      { label: 'Payment Settings', value: 'paymentSettings', icon: SettingsIcon, description: 'Control auto-payment processing and system flags' },
      { label: 'Initialize Commerce', value: 'commerceInit', icon: MoneyIcon, description: 'Onboard a business user to commerce by email' },
    ]
  },
  {
    title: 'Creator Management',
    icon: PersonIcon,
    color: 'secondary',
    items: [
      { label: 'Creators', value: 'creators', icon: PersonIcon, description: 'Manage creator profiles and relationships' },
      { label: 'Creator CRM', value: 'creatorCRM', icon: PersonIcon, description: 'Customer relationship management for creators' },
      { label: 'CRM Blocklist', value: 'blocklist', icon: PersonIcon, description: 'Manage blocked users and creators' },
    ]
  },
  {
    title: 'Platform Functions',
    icon: HandshakeIcon,
    color: 'error',
    items: [
      { label: 'Collabs', value: 'campaigns', icon: CampaignIcon, description: 'Create and manage ongoing platform collabs' },
      { label: 'Referral Suite', value: 'commissions', icon: MoneyIcon, description: 'Track referrals and commissions' },
      { label: 'Clients', value: 'clients', icon: BusinessIcon, description: 'Manage client relationships' },
    ]
  },
  {
    title: 'GTM',
    icon: CampaignIcon,
    color: 'success',
    items: [
      { label: 'Creator Partner Suite', value: 'brandemails', icon: EmailIcon, description: 'AI-powered Creator Agent' },
      { label: 'Marketing', value: 'marketing', icon: CampaignIcon, description: 'Marketing tools and strategies' },
      { label: 'Email From A Creator', value: 'blastemail', icon: EmailIcon, description: 'Send bulk email campaigns' },
      { label: 'Internal Campaigns', value: 'internalcampaigns', icon: CampaignIcon, description: 'Internal team campaigns' },
    ]
  },
  
  {
    title: 'Communication',
    icon: MessageIcon,
    color: 'info',
    items: [
      { label: 'SMS Channels', value: 'smsChannels', icon: MessageIcon, description: 'Manage SMS communication channels' },
      { label: 'SMS Blast', value: 'smsBlast', icon: MessageIcon, description: 'Send bulk SMS messages' },
      { label: 'Conversations', value: 'conversations', icon: MessageIcon, description: 'View and manage conversations' },
    ]
  },
  {
    title: 'System & Tools',
    icon: SettingsIcon,
    color: 'default',
    items: [
      { label: 'Apps & Services', value: 'services', icon: SettingsIcon, description: 'Manage integrated apps and services' },
      { label: 'Surveys', value: 'surveys', icon: AnalyticsIcon, description: 'Create and analyze surveys' },
      { label: 'Bolts', value: 'bolts', icon: SettingsIcon, description: 'System automation tools' },
      { label: 'Notifications', value: 'notificationsAdmin', icon: NotificationsIcon, description: 'Manage system notifications' },
      { label: 'Contracts', value: 'contracts', icon: ContractIcon, description: 'Contract management system' },
    ]
  },

   {
    title: 'Content Management',
    icon: ArticleIcon,
    color: 'secondary',
    items: [
      { 
        label: 'Articles', 
        value: 'articles', 
        icon: ArticleIcon, 
        description: 'Manage blog articles and content' 
      },
    ]
  },
];

// Helper function to ensure valid MUI colors
const getValidMUIColor = (color) => {
  const validColors = ['primary', 'secondary', 'error', 'warning', 'info', 'success', 'default'];
  return validColors.includes(color) ? color : 'primary';
};

const CompanyList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [viewMode, setViewMode] = useState('sections'); // 'sections' or 'list'
  const [expandedSections, setExpandedSections] = useState({});
  const [headerHeight, setHeaderHeight] = useState(80);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load sidebar state from localStorage
    const saved = localStorage.getItem('adminSidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  
  const currentView = searchParams.get('tab') || 'companies';
  const setCurrentView = (newValue) => {
    setSearchParams((params) => ({ ...params, tab: newValue }));
    setMobileNavOpen(false);
  };

  const { getCurrrentUser } = useAuth();

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('adminSidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Measure header height dynamically
  useEffect(() => {
    const measureHeader = () => {
      const header = document.getElementById('admin-header');
      if (header) {
        setHeaderHeight(header.offsetHeight);
      }
    };
    
    measureHeader();
    window.addEventListener('resize', measureHeader);
    return () => window.removeEventListener('resize', measureHeader);
  }, []);

  // Auto-expand sections on larger screens
  useEffect(() => {
    if (!isMobile && !isTablet) {
      const autoExpanded = {};
      navigationSections.forEach((_, index) => {
        autoExpanded[index] = true;
      });
      setExpandedSections(autoExpanded);
    }
  }, [isMobile, isTablet]);

  // Toggle sidebar collapse
  const toggleSidebar = () => {
    setSidebarCollapsed(prev => !prev);
  };

  // Find current view details for breadcrumb
  const getCurrentViewDetails = () => {
    for (const section of navigationSections) {
      const item = section.items.find(item => item.value === currentView);
      if (item) {
        return { 
          section: section.title, 
          item: item.label, 
          sectionColor: getValidMUIColor(section.color)
        };
      }
    }
    return { section: 'Dashboard', item: 'Overview', sectionColor: 'primary' };
  };

  const handleSectionToggle = (sectionIndex) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionIndex]: !prev[sectionIndex]
    }));
  };

  const renderView = () => {
    switch (currentView) {
      case 'creators': return <CreatorDetailsView />;
      case 'system-overview': return <BlitzSystemOverview />;
      case 'blocklist': return <Blocklist />;
      case 'admin': return <UserDetailsView />;
      case 'campaigns': return <UnifiedCollaborationAdminView />;
      case 'payouts': return <PayoutDetailsView />;
      case 'invoices': return <InvoiceDetailsView />;
      case 'marketing': return <MarketingView />;
      case 'blastemail': return <BlastEmail />;
      case 'creatorCRM': return <CreatorCRMView />;
      case 'companies': return <CompanyDetailsView />;
      case 'users': return <UserAdminView />;
      case 'conversations': return <ConversationsAdminView />;
      case 'commissions': return <ReferralDetailsView />;
      case 'smsChannels': return <SMSChannels />;
      case 'smsBlast': return <CreatorSMS />;
      case 'clients': return <ClientDetailsView />;
      case 'creatorEmails': return <CreatorEmailView />;
      case 'internalcampaigns': return <InternalCampaigns />;
      case 'brandemails': return <BrandEmails />;
      case 'services': return <AdminServices />;
      case 'withdrawals': return <Withdrawals />;
      case 'surveys': return <SurveyDetailsView />;
      case 'bolts': return <BlitzBoltDetailsView />;
      case 'notificationsAdmin': return <NotificationsDetailsView />;
      case 'contracts': return <ContractDetailsView />;
      case 'noa': return <NOADetailsView />;
      case 'collections': return <CollectionsDetailsView />;
      case 'articles': return <ArticleAdminView />;
      case 'bankAccounts': return <BankAccountsSection />;
      case 'platformPaymentMethods': return <PlatformPaymentMethodsSection />;
      case 'userBalances': return <UserBalancesView />;
      case 'paymentSettings': return <PaymentSettingsView />;
      case 'commerceInit': return <CommerceInitView />;
      default: return <CompanyDetailsView />;
    }
  };

  // Mobile Speed Dial Actions
  const speedDialActions = navigationSections.flatMap(section => 
    section.items.map(item => ({
      icon: <item.icon />,
      name: item.label,
      onClick: () => setCurrentView(item.value),
      color: getValidMUIColor(section.color)
    }))
  );

  // Navigation Component (used in both mobile drawer and desktop layout)
  const NavigationContent = ({ isMobileView = false }) => (
    <Box sx={{ 
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {isMobileView && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Admin Navigation
          </Typography>
          <IconButton onClick={() => setMobileNavOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}
      
      <Box sx={{ 
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        p: isMobileView ? 1.5 : 0,
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.3)',
          }
        }
      }}>
        {viewMode === 'sections' ? (
          <Box>
            {navigationSections.map((section, sectionIndex) => {
              const validColor = getValidMUIColor(section.color);
              return (
                <Paper
                  key={section.title}
                  elevation={1}
                  sx={{
                    mb: 1.5,
                    overflow: 'hidden',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <ListItemButton
                    onClick={() => handleSectionToggle(sectionIndex)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      backgroundColor: `${validColor}.light`,
                      '&:hover': {
                        backgroundColor: `${validColor}.main`,
                        color: 'white',
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <section.icon />
                    </ListItemIcon>
                    <ListItemText
                      primary={section.title}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}
                    />
                    {expandedSections[sectionIndex] ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>

                  <Collapse in={expandedSections[sectionIndex]} timeout="auto" unmountOnExit>
                    <List dense sx={{ bgcolor: 'background.paper', pb: 1 }}>
                      {section.items.map((item) => (
                        <ListItem key={item.value} disablePadding>
                          <ListItemButton
                            selected={currentView === item.value}
                            onClick={() => setCurrentView(item.value)}
                            sx={{
                              py: 1,
                              px: 3,
                              borderRadius: 1,
                              mx: 1,
                              mb: 0.5,
                              '&.Mui-selected': {
                                backgroundColor: `${validColor}.main`,
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: `${validColor}.dark`,
                                },
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <item.icon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={item.label}
                              secondary={!isMobileView ? item.description : undefined}
                              primaryTypographyProps={{
                                fontSize: '0.85rem',
                                fontWeight: currentView === item.value ? 600 : 400
                              }}
                              secondaryTypographyProps={{
                                fontSize: '0.7rem'
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </Paper>
              );
            })}
          </Box>
        ) : (
          <List dense>
            {navigationSections.flatMap(section => 
              section.items.map(item => (
                <ListItem key={item.value} disablePadding>
                  <ListItemButton
                    selected={currentView === item.value}
                    onClick={() => setCurrentView(item.value)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <item.icon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
        )}
      </Box>
    </Box>
  );

  if ((getCurrrentUser()?.type ?? 'none') !== 'admin') {
    return (
      <Container maxWidth="sm" sx={{ 
        textAlign: 'center', 
        height: '90vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography color="textSecondary">
            Sorry, you are not allowed to view this page.
          </Typography>
        </Paper>
      </Container>
    );
  }

  const currentDetails = getCurrentViewDetails();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header Section */}
      <Paper 
        id="admin-header"
        elevation={0} 
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1100
        }}
      >
        <Container maxWidth={false} sx={{ py: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            {/* Left side - Breadcrumbs and title */}
            <Box sx={{ flex: 1 }}>
              <Breadcrumbs sx={{ mb: 0.5 }}>
                <Link 
                  color="inherit" 
                  href="#" 
                  onClick={() => setCurrentView('companies')}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <DashboardIcon fontSize="small" />
                  Admin Dashboard
                </Link>
                <Typography color="text.secondary" fontSize="small">
                  {currentDetails.section}
                </Typography>
                <Typography color={`${currentDetails.sectionColor}.main`} fontSize="small" fontWeight={600}>
                  {currentDetails.item}
                </Typography>
              </Breadcrumbs>
              
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="h5" fontWeight={700}>
                  {currentDetails.item}
                </Typography>
                <Chip 
                  label={getCurrrentUser()?.name || 'Admin'} 
                  size="small" 
                  color={currentDetails.sectionColor}
                  variant="outlined"
                />
              </Stack>
            </Box>

            {/* Right side - Controls */}
            <Stack direction="row" alignItems="center" gap={1}>
              {/* Desktop Sidebar Toggle */}
              {!isMobile && (
                <Tooltip title={sidebarCollapsed ? "Show Sidebar" : "Hide Sidebar"}>
                  <IconButton
                    onClick={toggleSidebar}
                    color="primary"
                    size="small"
                    sx={{ 
                      border: 1, 
                      borderColor: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.light' }
                    }}
                  >
                    {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                  </IconButton>
                </Tooltip>
              )}
              
              {/* Desktop View Mode Toggle */}
              {!isMobile && !sidebarCollapsed && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={viewMode === 'sections' ? <ListIcon /> : <ViewModuleIcon />}
                  onClick={() => setViewMode(viewMode === 'sections' ? 'list' : 'sections')}
                  sx={{ textTransform: 'none' }}
                >
                  {viewMode === 'sections' ? 'List View' : 'Section View'}
                </Button>
              )}
              
              {/* Mobile Navigation Button */}
              {isMobile && (
                <IconButton
                  color="primary"
                  onClick={() => setMobileNavOpen(true)}
                  sx={{ 
                    border: 1, 
                    borderColor: 'primary.main',
                    '&:hover': { backgroundColor: 'primary.light' }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Stack>
          </Stack>
        </Container>
      </Paper>

      <Container maxWidth={false} sx={{ py: 0 }}>
        <Grid container spacing={0}>
          {/* Desktop Sidebar */}
          {!isMobile && (
            <Grid 
              item 
              xs={12} 
              md={sidebarCollapsed ? 0 : 3} 
              lg={sidebarCollapsed ? 0 : 2.5}
              sx={{
                transition: theme.transitions.create(['all'], {
                  duration: theme.transitions.duration.standard,
                }),
                overflow: 'hidden',
              }}
            >
              {!sidebarCollapsed && (
                <Box sx={{ 
                  position: 'sticky',
                  top: `${headerHeight}px`,
                  height: `calc(100vh - ${headerHeight}px)`,
                  py: 2,
                  pr: 2,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <NavigationContent />
                </Box>
              )}
            </Grid>
          )}

          {/* Main Content */}
          <Grid 
            item 
            xs={12} 
            md={isMobile ? 12 : (sidebarCollapsed ? 12 : 9)} 
            lg={isMobile ? 12 : (sidebarCollapsed ? 12 : 9.5)}
            sx={{
              transition: theme.transitions.create(['all'], {
                duration: theme.transitions.duration.standard,
              }),
            }}
          >
            <Box sx={{ p: 2, minHeight: '100%' }}>
              {renderView()}
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Floating Expand Button (when sidebar is collapsed on desktop) */}
      {!isMobile && sidebarCollapsed && (
        <Zoom in={sidebarCollapsed}>
          <Fab
            color="primary"
            size="small"
            onClick={toggleSidebar}
            sx={{
              position: 'fixed',
              left: 16,
              top: `calc(${headerHeight}px + 16px)`,
              zIndex: 1000,
            }}
          >
            <ChevronRightIcon />
          </Fab>
        </Zoom>
      )}

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: '85vw',
            maxWidth: 360,
          },
        }}
      >
        <NavigationContent isMobileView />
      </Drawer>

      {/* Mobile Speed Dial */}
      {isMobile && (
        <SpeedDial
          ariaLabel="Quick Navigation"
          sx={{ 
            position: 'fixed', 
            bottom: `calc(${APPSHELL_BAR_HEIGHT}px + ${SPEED_DIAL_MARGIN}px + env(safe-area-inset-bottom, 0px))`,
            left: 16,
            zIndex: (theme) => theme.zIndex.speedDial
          }}
          icon={<SpeedDialIcon />}
          direction="up"
        >
          {speedDialActions.slice(0, 8).map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={action.onClick}
            />
          ))}
        </SpeedDial>
      )}
    </Box>
  );
};

export default CompanyList;